import { useMutation, useQuery, useQueryClient, type UseMutationOptions, type UseQueryOptions } from '@tanstack/react-query';
import { getApiService, ServiceType } from './apiServices';
import { NOTIFICATION_ENDPOINTS } from './apiEndPoints';
import { getAuth0Token } from "../auth/utils";


// Types
interface SignalRNegotiateRequest {
  user_id: number;
}

interface SignalRNegotiateResponse {
  url: string;
  accessToken?: string;
}

interface SendMessageRequest {
  user_id: string;
  type: string;
  title: string;
  message: string;
}

interface SignalRStatusResponse {
  isConnected: boolean;
  connectionId?: string;
  userId?: number;
  status: string;
}

interface ChatRoom {
  id: number;
  order_id: number;
  type: string;
  title: string;
  created_at: string;
  updated_at: string;
  participants: Array<{
    user_id: number;
    full_name: string;
    profile_image: string | null;
    joined_at: string;
    rating?: number;
  }>;
  last_message?: {
    id: number;
    message: string;
    message_type: string;
    sender: {
      id: number;
      full_name: string;
      profile_image: string | null;
    };
    created_at: string;
    is_read: boolean;
  };
  stats?: {
    participant_count: number;
    message_count: number;
    unread_count: number;
  };
}

interface ChatMessage {
  id: number;
  chat_room_id: number;
  message: string;
  message_type: string;
  sender: {
    id: number;
    full_name: string;
    profile_image: string | null;
  };
  created_at: string;
  is_read: boolean;
}

interface CreateChatRoomRequest {
  order_id: number;
  type: string;
  participant_ids: number[];
}

interface JoinChatRoomRequest {
  chat_room_id: number;
  user_id: number;
}

interface SendChatMessageRequest {
  chat_room_id: number;
  sender_id: number;
  message: string;
  message_type: string;
}

interface GetMessagesRequest {
  roomId: number;
  limit?: number;
  offset?: number;
}

interface MarkMessagesReadRequest {
  chat_room_id: number;
  user_id: number;
  message_ids?: number[];
}

interface GetMissedNotificationsRequest {
  userId: number;
  limit?: number;
  initiator_type?: 'sender' | 'traveler';
}

interface BackendNotification {
  id: number;
  user_id: number;
  type: string;
  title: string;
  message: string;
  initiator_type: 'sender' | 'traveler';
  delivery_method: string;
  data?: {
    type?: string;
    order_code?: string;
    shipment_id?: number;
    itinerary_id?: number;
    [key: string]: unknown;
  };
  is_read: boolean;
  created_at: string;
  updated_at: string;
}

interface GetMissedNotificationsResponse {
  success: boolean;
  message: string;
  data: {
    total_notifications: number;
    unread_count: number;
    notifications: BackendNotification[];
  };
}

// Query Keys
export const notificationKeys = {
  all: ['notifications'] as const,
  signalRStatus: () => [...notificationKeys.all, 'signalr-status'] as const,
  chatRooms: (userId: number) => [...notificationKeys.all, 'chat-rooms', userId] as const,
  chatMessages: (roomId: number, limit?: number, offset?: number) =>
    [...notificationKeys.all, 'chat-messages', roomId, { limit, offset }] as const,
  missedNotifications: (userId: number, limit?: number, initiator_type?: string) =>
    [...notificationKeys.all, 'missed-notifications', userId, { limit, initiator_type }] as const,
};

// Base API Function
async function notificationRequest<T>({
  url,
  method = 'GET',
  body,
}: {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
}): Promise<T> {
  try {
    const notificationService = getApiService(ServiceType.NOTIFICATION);
    const token = getAuth0Token();

    if (token) {
      notificationService.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
      notificationService.defaults.headers.common['Content-Type'] = 'application/json';
    }

    const response = await notificationService({
      url,
      method,
      data: body,
    });

    return response.data;
  } catch (error: unknown) {
    throw error;
  }
}

// API Functions
const signalRNegotiate = async (data: SignalRNegotiateRequest): Promise<SignalRNegotiateResponse> => {
  return notificationRequest({
    url: NOTIFICATION_ENDPOINTS.SIGNALR_NEGOTIATE.path,
    method: 'POST',
    body: { user_id: Number(data.user_id) },
  });
};

const sendMessage = async (data: SendMessageRequest): Promise<unknown> => {
  return notificationRequest({
    url: NOTIFICATION_ENDPOINTS.SEND_MESSAGE.path,
    method: 'POST',
    body: data,
  });
};

const getSignalRStatus = async (): Promise<SignalRStatusResponse> => {
  return notificationRequest({
    url: NOTIFICATION_ENDPOINTS.SIGNALR_STATUS.path,
    method: 'GET',
  });
};

const createChatRoom = async (data: CreateChatRoomRequest): Promise<unknown> => {
  return notificationRequest({
    url: NOTIFICATION_ENDPOINTS.CREATE_CHAT_ROOM.path,
    method: 'POST',
    body: data,
  });
};

const joinChatRoom = async (data: JoinChatRoomRequest): Promise<unknown> => {
  return notificationRequest({
    url: NOTIFICATION_ENDPOINTS.JOIN_CHAT_ROOM.path,
    method: 'POST',
    body: data,
  });
};

const getUserChatRooms = async (userId: number): Promise<{ data: { chat_rooms: ChatRoom[] } }> => {
  return notificationRequest({
    url: NOTIFICATION_ENDPOINTS.GET_USER_CHAT_ROOMS.path.replace(':userId', userId.toString()),
    method: 'GET',
  });
};

const sendChatMessage = async (data: SendChatMessageRequest): Promise<unknown> => {
  return notificationRequest({
    url: NOTIFICATION_ENDPOINTS.SEND_CHAT_MESSAGE.path,
    method: 'POST',
    body: data,
  });
};

const getChatMessages = async ({ roomId, limit = 50, offset = 0 }: GetMessagesRequest): Promise<{ data: { messages: ChatMessage[] } }> => {
  return notificationRequest({
    url: `${NOTIFICATION_ENDPOINTS.GET_CHAT_MESSAGES.path.replace(':roomId', roomId.toString())}?limit=${limit}&offset=${offset}`,
    method: 'GET',
  });
};

const markMessagesAsRead = async (data: MarkMessagesReadRequest): Promise<unknown> => {
  return notificationRequest({
    url: NOTIFICATION_ENDPOINTS.MARK_MESSAGES_READ.path,
    method: 'POST',
    body: data,
  });
};

export const getMissedNotifications = async ({ userId, limit = 30, initiator_type }: GetMissedNotificationsRequest): Promise<GetMissedNotificationsResponse> => {
  let url = NOTIFICATION_ENDPOINTS.GET_MISSED_NOTIFICATIONS.path.replace(':userId', userId.toString());

  const queryParams = new URLSearchParams();
  if (limit) queryParams.append('limit', limit.toString());
  if (initiator_type) queryParams.append('initiator_type', initiator_type);

  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }

  return notificationRequest({
    url,
    method: 'GET',
  });
};

export const markNotificationAsRead = async (notificationId: unknown): Promise<void> => {
  const url = NOTIFICATION_ENDPOINTS.MARK_NOTIFICATION_READ.path.replace(':notificationId', String(notificationId));

  return notificationRequest<void>({
    url,
    method: 'PATCH',
  });
};

// Custom Hooks

// Mutations
export const useSignalRNegotiate = (options?: UseMutationOptions<SignalRNegotiateResponse, Error, SignalRNegotiateRequest>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: signalRNegotiate,
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.signalRStatus() });
    },
    ...options,
  });
};

export const useSendMessage = (options?: UseMutationOptions<unknown, Error, SendMessageRequest>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: sendMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    ...options,
  });
};

export const useCreateChatRoom = (options?: UseMutationOptions<unknown, Error, CreateChatRoomRequest>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createChatRoom,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    ...options,
  });
};

export const useJoinChatRoom = (options?: UseMutationOptions<unknown, Error, JoinChatRoomRequest>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: joinChatRoom,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    ...options,
  });
};

export const useSendChatMessage = (options?: UseMutationOptions<unknown, Error, SendChatMessageRequest>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendChatMessage,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.chatMessages(variables.chat_room_id) });
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    ...options,
  });
};

export const useMarkMessagesAsRead = (options?: UseMutationOptions<unknown, Error, MarkMessagesReadRequest>) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: markMessagesAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    ...options,
  });
};

// Queries
export const useGetSignalRStatus = (options?: Omit<UseQueryOptions<SignalRStatusResponse, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: notificationKeys.signalRStatus(),
    queryFn: getSignalRStatus,
    ...options,
  });
};

export const useGetUserChatRooms = (
  userId: number,
  options?: Omit<UseQueryOptions<{ data: { chat_rooms: ChatRoom[] } }, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: notificationKeys.chatRooms(userId),
    queryFn: () => getUserChatRooms(userId),
    enabled: !!userId,
    ...options,
  });
};

export const useGetChatMessages = (
  params: GetMessagesRequest,
  options?: Omit<UseQueryOptions<{ data: { messages: ChatMessage[] } }, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: notificationKeys.chatMessages(params.roomId, params.limit, params.offset),
    queryFn: () => getChatMessages(params),
    enabled: !!params.roomId,
    ...options,
  });
};

export const useGetMissedNotifications = (
  params: GetMissedNotificationsRequest,
  options?: Omit<UseQueryOptions<GetMissedNotificationsResponse, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: notificationKeys.missedNotifications(params.userId, params.limit, params.initiator_type),
    queryFn: () => getMissedNotifications(params),
    enabled: !!params.userId,
    ...options,
  });
};

export const useMarkNotificationAsRead = (
  options?: UseMutationOptions<void, Error, unknown>
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, unknown>({
    mutationFn: markNotificationAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationKeys.all,
      });
    },
    ...options,
  });
};

// Export types for external use
export type { BackendNotification, GetMissedNotificationsResponse, GetMissedNotificationsRequest };